import {
    Component,
    OnInit,
    OnDestroy,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    inject,
    signal,
    effect,
} from '@angular/core'
import { Subject } from 'rxjs'
import { takeUntil, map } from 'rxjs/operators'
import { valueConstants } from '@app/core/constants/valueConstants'
import { LeadsService } from '../../../../features/crm/services/lead.service'
import { LeadService } from '../../../../features/crm/services/lead-info.service'
import {
    IUsersLeadsApiData,
    IUserLeadItemApi,
    ILeadConversionMetrics,
} from '../../../../features/crm/models/lead.model'
import { mapStageNameToStage } from '../../../../features/crm/helpers/lead-list.helper'
import { ELeadStageTitle } from '@app/core/enum/client.enum'
import { fetchUsersLeadsWithProducts } from '../../../../core/helpers/leads-data.helper'
import { UserStore } from '@core/services/user/user.store'
import { getUserId } from '@app/core/helpers/user.helper'
import { IDashboardService } from '@core/services/dashboard/dashboard.service'
import { ICON_PATHS } from '@app/core/constants/assets.constants'
import { EiTooltipDirective } from '../../../../shared/directives'
@Component({
    selector: 'app-lead-conversion-metrics',
    templateUrl: './lead-conversion-metrics.component.html',
    styleUrls: ['./lead-conversion-metrics.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EiTooltipDirective],
})
export class LeadConversionMetricsComponent implements OnInit, OnDestroy {
    private readonly cdr = inject(ChangeDetectorRef)
    private readonly leadsService = inject(LeadsService)
    private readonly leadInfoService = inject(LeadService)
    private readonly userStore = inject(UserStore)
    private readonly dashboardService = inject(IDashboardService)

    readonly isLoading = signal<boolean>(true)
    readonly error = signal<string | null>(null)
    readonly metrics = signal<ILeadConversionMetrics>({
        totalLeads: 0,
        pipelineLeads: 0,
        wonLeads: 0,
        conversionRatio: '0:0:0',
        leadToWonPercentage: 0,
    })

    readonly valueConstants = valueConstants
    readonly infoIcon = ICON_PATHS.INFO
    readonly skeletonItems = Array(3).fill(0)

    private destroy$ = new Subject<void>()
    private lastAcademicYearStart: number | null = null

    constructor() {
        effect(
            () => {
                const academicYearStart = this.dashboardService.selectedAcademicYearStart()
                if (this.lastAcademicYearStart === null) {
                    this.lastAcademicYearStart = academicYearStart
                    return
                }
                if (academicYearStart === this.lastAcademicYearStart) {
                    return
                }
                this.lastAcademicYearStart = academicYearStart
                this.loadMetrics()
            },
            { allowSignalWrites: true },
        )
    }

    ngOnInit(): void {
        this.loadMetrics()
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    private loadMetrics(): void {
        this.isLoading.set(true)
        this.error.set(null)

        const user = this.userStore.getUserData()
        if (!user) {
            this.error.set('User data not available')
            this.isLoading.set(false)
            this.cdr.markForCheck()
            return
        }

        const userId = getUserId(this.userStore)
        if (!userId) {
            this.error.set('User ID not available')
            this.isLoading.set(false)
            this.cdr.markForCheck()
            return
        }

        // Fetch all leads for the user (RM or ZM) and products using shared helper
        // For ZM, we get all reporting RMs' leads; for RM, we get their own leads
        fetchUsersLeadsWithProducts(
            userId,
            this.leadsService,
            this.leadInfoService,
            this.dashboardService.selectedAcademicYearStart(),
        )
            .pipe(
                map(([data, productMap]) => {
                    return this.calculateMetrics(data)
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (calculatedMetrics) => {
                    this.metrics.set(calculatedMetrics)
                    this.isLoading.set(false)
                    this.cdr.markForCheck()
                },
                error: () => {
                    this.error.set('Failed to load conversion metrics')
                    this.isLoading.set(false)
                    this.cdr.markForCheck()
                },
            })
    }

    private calculateMetrics(data: IUsersLeadsApiData): ILeadConversionMetrics {
        // Collect all leads from all users
        let allLeads: IUserLeadItemApi[] = []
        if (data.user_leads) {
            for (const user of data.user_leads) {
                if (user.leads) {
                    allLeads = allLeads.concat(user.leads)
                }
            }
        }

        // Total leads: All leads that reached S1 or beyond (all leads in the system)
        const totalLeads = allLeads.length

        // Pipeline: Leads in stages S3, S4, S5, S6 (backend: Demo Completed, Proposal Submitted, Contract Negotiation, SSF Signed)
        const pipelineStages: ELeadStageTitle[] = [
            ELeadStageTitle.S3,
            ELeadStageTitle.S4,
            ELeadStageTitle.S5,
            ELeadStageTitle.S6,
        ]
        const pipelineLeads = allLeads.filter((lead: IUserLeadItemApi) => {
            const stage = mapStageNameToStage(lead.current_stage?.stage_name ?? '')
            return pipelineStages.includes(stage)
        }).length

        // Won: Leads in stage S7 (Deal Won)
        const wonLeads = allLeads.filter((lead: IUserLeadItemApi) => {
            const stage = mapStageNameToStage(lead.current_stage?.stage_name ?? '')
            return stage === ELeadStageTitle.S7
        }).length

        // Calculate Lead Conversion Ratio (Total Leads : Pipeline : Won)
        // Simplify to lowest terms by finding GCD
        const conversionRatio = this.calculateConversionRatio(totalLeads, pipelineLeads, wonLeads)

        // Calculate Lead to Won % = (Won / Total Leads that Reached S1 or Beyond) * 100
        const leadToWonPercentage =
            totalLeads > 0 ? Math.round((wonLeads / totalLeads) * 100 * 100) / 100 : 0

        return {
            totalLeads,
            pipelineLeads,
            wonLeads,
            conversionRatio,
            leadToWonPercentage,
        }
    }

    private calculateConversionRatio(total: number, pipeline: number, won: number): string {
        if (!total && !pipeline && !won) {
            return '0:0:0'
        }

        // Find GCD of all three numbers to simplify the ratio
        const gcd = this.findGCD(this.findGCD(total, pipeline), won)

        if (!gcd) {
            return `${total}:${pipeline}:${won}`
        }

        const simplifiedTotal = Math.round(total / gcd)
        const simplifiedPipeline = Math.round(pipeline / gcd)
        const simplifiedWon = Math.round(won / gcd)

        return `${simplifiedTotal}:${simplifiedPipeline}:${simplifiedWon}`
    }

    private findGCD(value1: number, value2: number): number {
        if (!value2) {
            return value1
        }
        return this.findGCD(value2, value1 % value2)
    }

    getTooltipText(metric: string): string {
        switch (metric) {
            case 'ratio':
                return 'Conversion ratio among the three stages: Lead Prospecting : Active Pipeline : Deals Won'
            case 'percentage':
                return 'The percentage of leads created (S1) that converted to Deals won (SW)'
            default:
                return ''
        }
    }
}
